import { type RefObject } from 'react';

import classNames from '@/utils/classnames';

import { usePanelResize } from '../hooks/usePanelResize';

/** How far one arrow-key press moves the divider. */
const KEY_STEP = 16;

interface ResizeHandleProps {
  /** The panel this divider resizes. */
  panelRef: RefObject<HTMLElement | null>;
  /** Current width, so keyboard steps have something to move from. */
  width: number;
  onWidth: (width: number) => void;
  min: number;
  max: number | (() => number);
  /** Which edge of the panel this sits on — `start` means dragging left widens it. */
  edge: 'start' | 'end';
  onCollapse?: () => void;
  /** Double-click / Home target. Omit to disable resetting. */
  onReset?: () => void;
  label: string;
}

/**
 * The divider between two panels — and the only thing drawing a line between
 * them, so the panels either side must not carry their own border.
 *
 * It is 1px wide but grabbable across 7px: a `-inset-x-0.75` overlay widens the
 * hit area without taking any layout space. `z-panel-resizer` puts that overlay
 * above the keep-alive noVNC surface, which is otherwise painted over the top of
 * the whole panel body and would make the handle unclickable on the Desktop tab.
 */
export function ResizeHandle({
  panelRef,
  width,
  onWidth,
  min,
  max,
  edge,
  onCollapse,
  onReset,
  label,
}: ResizeHandleProps) {
  const { dragging, handleProps } = usePanelResize({ panelRef, onWidth, min, max, edge, onCollapse });

  const step = (direction: -1 | 1) => {
    const upper = typeof max === 'function' ? max() : max;
    const delta = (edge === 'start' ? -direction : direction) * KEY_STEP;
    onWidth(Math.round(Math.min(Math.max(width + delta, min), Math.max(upper, min))));
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={Math.round(width)}
      aria-valuemin={min}
      tabIndex={0}
      className={classNames(
        'relative z-panel-resizer w-px shrink-0 select-none transition-colors',
        dragging ? 'bg-brand' : 'bg-line-subtle',
      )}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          step(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          step(1);
        } else if (e.key === 'Home' && onReset) {
          e.preventDefault();
          onReset();
        }
      }}
      {...handleProps}
    >
      <div
        // `touch-action: none` is required, or a touch/trackpad drag scrolls the
        // page instead of producing pointermove events.
        className="absolute inset-y-0 -inset-x-0.75 cursor-col-resize touch-none hover:bg-brand-border"
        onDoubleClick={onReset}
      />
      {dragging && (
        // Covers the viewport for the duration of the drag: kills text selection
        // and stops the noVNC iframe from stealing hover/cursor. An overlay
        // rather than mutating `document.body.style`, so an unmount mid-drag
        // (snap-collapse does exactly that) can't leave the page in that state.
        <div className="fixed inset-0 z-panel-resizer cursor-col-resize" />
      )}
    </div>
  );
}
